import { T, useGT } from 'gt-react';
import { CaretDownIcon } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ForecastRunSummary } from 'src/api/rpc/hooks/procurement/useForecastRuns';
import { AddButton, ControlChip, ControlRow } from './ControlRow';
import { runLabel, type DemandSearch } from './demandFilters';

/**
 * The Demand row: which data the grid is showing. The first chip is the run
 * itself; the rest are comparisons, and the `+` adds another.
 *
 * Comparisons live here rather than with the View controls because they change
 * WHAT data is on screen, not how it is drawn — the same reason the run picker
 * sits at the head of this row instead of in the page header.
 */
export function DemandSeriesRow({
  search,
  runs,
  activeRunId,
  asOf,
  onChange,
  onOpenRunHistory,
}: {
  search: DemandSearch;
  runs: ForecastRunSummary[];
  activeRunId: string | undefined;
  /** The run's data cutoff, which is not the same as when it ran. */
  asOf: string | undefined;
  onChange: (next: DemandSearch) => void;
  /** Opens the run's own diagnostics, which are a route of their own. */
  onOpenRunHistory: () => void;
}) {
  const gt = useGT();
  const compare = search.compare ?? [];
  const activeRun = runs.find((run) => run.id === activeRunId);

  return (
    <ControlRow label={gt('Demand', { context: 'demand control row label' })}>
      {/* The run chip is a menu, not a removable comparison: there is always
          exactly one run being read. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex h-7 items-center gap-1 rounded-md border bg-background px-2 text-xs hover:bg-muted/50"
          >
            {activeRun ? (
              gt('Forecast run {when}', {
                context: 'demand series run chip',
                when: runLabel(activeRun.startedAt),
              })
            ) : (
              <T context="demand series run chip without a run">Latest run</T>
            )}
            <CaretDownIcon className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          <DropdownMenuLabel className="text-xs">
            <T context="run picker group">Forecast run</T>
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={activeRunId ?? ''}
            onValueChange={(value) =>
              onChange({
                ...search,
                runId: value,
                compare: search.compare?.filter((entry) => entry !== value),
              })
            }
          >
            <ForecastRunRadioOptions runs={runs} />
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-xs" onSelect={onOpenRunHistory}>
            <T context="run picker action">Run health</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {asOf ? (
        <span className="text-xs text-muted-foreground">
          {gt('data as of {asOf}', {
            context: 'demand series as-of date',
            asOf,
          })}
        </span>
      ) : null}

      <DemandComparisonControls
        compare={compare}
        runs={runs}
        activeRunId={activeRunId}
        maxComparisons={3}
        onChange={(next) =>
          onChange({
            ...search,
            compare: next,
            history: next?.includes('actuals') ? search.history : undefined,
          })
        }
      />
    </ControlRow>
  );
}

export function ForecastRunRadioOptions({
  runs,
  includeLatest = false,
}: {
  runs: ForecastRunSummary[];
  includeLatest?: boolean;
}) {
  return (
    <>
      {includeLatest ? (
        <DropdownMenuRadioItem value="latest" className="text-xs">
          <T context="forecast run picker latest option">Latest Run</T>
        </DropdownMenuRadioItem>
      ) : null}
      {runs.map((run) => (
        <DropdownMenuRadioItem key={run.id} value={run.id} className="text-xs">
          {runLabel(run.startedAt)}
          <span className="ml-1 text-muted-foreground">
            {run.purpose}
            {' · '}
            {run.status}
          </span>
        </DropdownMenuRadioItem>
      ))}
    </>
  );
}

export function DemandComparisonControls({
  compare,
  runs,
  activeRunId,
  priorOnly = false,
  maxComparisons = Number.POSITIVE_INFINITY,
  onChange,
}: {
  compare: string[];
  runs: ForecastRunSummary[];
  activeRunId: string | undefined;
  priorOnly?: boolean;
  maxComparisons?: number;
  onChange: (next: string[] | undefined) => void;
}) {
  const gt = useGT();
  const normalized = [...new Set(compare)]
    .filter((entry) => entry !== activeRunId)
    .slice(0, maxComparisons);
  const selected = new Set(normalized);
  const activeRun = runs.find((run) => run.id === activeRunId);
  const comparable = runs.filter(
    (run) =>
      run.id !== activeRunId &&
      !selected.has(run.id) &&
      (!priorOnly || !activeRun || run.startedAt < activeRun.startedAt),
  );
  const canAdd = normalized.length < maxComparisons;
  const hasActuals = selected.has('actuals');
  const addDisabled = !canAdd || (hasActuals && comparable.length === 0);

  function toggle(entry: string) {
    const next = selected.has(entry)
      ? normalized.filter((other) => other !== entry)
      : [...normalized, entry].slice(0, maxComparisons);
    onChange(next.length ? next : undefined);
  }

  return (
    <>
      {normalized.map((entry) => (
        <ControlChip
          key={entry}
          onRemove={() => toggle(entry)}
          removeLabel={gt('Remove this comparison', {
            context: 'demand series chip remove',
          })}
        >
          {entry === 'actuals'
            ? gt('Actual', { context: 'demand comparison chip' })
            : runDisplayName(entry, runs, gt)}
        </ControlChip>
      ))}
      <Popover>
        <PopoverTrigger asChild>
          <AddButton
            label={gt('Add a comparison', {
              context: 'demand series row add',
            })}
            disabled={addDisabled}
          />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-1">
          {!hasActuals ? (
            <button
              type="button"
              onClick={() => toggle('actuals')}
              className="w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
            >
              <T context="demand compare option">Actual</T>
              <span className="block text-xs text-muted-foreground">
                <T context="demand compare option hint">
                  Only months that have closed and been scored.
                </T>
              </span>
            </button>
          ) : null}
          {comparable.length > 0 ? (
            <div className="max-h-56 overflow-y-auto">
              {comparable.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => toggle(run.id)}
                  className="w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted"
                >
                  {runLabel(run.startedAt)}
                  <span className="block text-xs text-muted-foreground">
                    {gt('{purpose} · {status}', {
                      context: 'demand compare run hint',
                      purpose: run.purpose,
                      status: run.status,
                    })}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </>
  );
}

function runDisplayName(
  runId: string,
  runs: ForecastRunSummary[],
  gt: ReturnType<typeof useGT>,
): string {
  const run = runs.find((candidate) => candidate.id === runId);
  return run
    ? gt('Forecast run {when}', {
        context: 'demand comparison chip',
        when: runLabel(run.startedAt),
      })
    : gt('Run …{id}', {
        context: 'demand comparison chip without loaded metadata',
        id: runId.slice(-8),
      });
}
